import { Badge, Button, Color, Icon, Radius, Select, Spacing } from 'zens-storybook'
import React, { useEffect } from 'react'
import { StyleSheet, View, useWindowDimensions } from 'react-native'
import { Workspace, useFloors, useWorkspaces } from 'zens-store'
import { FloorSelect } from 'zens-fragment'
import { Href, Link } from 'zens-router'

import { useTranslation } from 'react-i18next'

import { FloorplanView, FloorplanZoomView } from '../floorplan'
import { ZoneWorkspaceEmpty } from '../zone'

import LegendState from './legendstate'
import { colorForWorkspaceStatus } from './statuscolor'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  head: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: Spacing.S,
    zIndex: 10,
  },
  buttons: {
    flexDirection: 'row',
  },
  button: {
    marginLeft: Spacing.XS,
  },
  buttonInactive: {
    borderColor: Color.calmGrey,
  },
  iconInactive: {
    color: Color.metalGrey,
  },
  floorplan: {
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
    padding: Spacing.S,
    paddingBottom: 70 + Spacing.S,
  },
  skeletonButton: {
    minWidth: 0,
  },
  skeletonFloorplan: {
    backgroundColor: Color.simplyWhite,
  },
  skeletonSelect: {
    maxWidth: 200,
  },
  loading: {
    position: 'absolute',
    bottom: Spacing.M,
    right: Spacing.M,
  },
  legend: {
    position: 'absolute',
    left: 50 + Spacing.S,
    bottom: Spacing.S,
  },
})

export interface Props {
  companyId: number
  zoneId?: number
  search?: string
  onSelect?: (workspace: Workspace) => void
  floorId?: number
  onChangeFloor?: (floorId: number) => void
  onAdd?: () => void
  typeHref?: (type: 'table' | 'floorplan') => Href
}

const WorkspaceFloorplanSelect = (props: Props) => {
  const { companyId, zoneId = 0, search, onSelect, floorId = 0, onChangeFloor, onAdd, typeHref } = props
  const { height } = useWindowDimensions()
  const floors = useFloors({ companyId })
  const { t } = useTranslation()

  const workspaces = useWorkspaces({ companyId, filter: { floorId: [floorId] } }, { enabled: !!floorId })
  const workspacesSearch = useWorkspaces(
    { companyId, filter: { search }, pagination: { page: 0, size: 10 } },
    { enabled: !!search },
  )
  const workspacesZone = useWorkspaces({ companyId, filter: { zoneId: [zoneId] } }, { enabled: !!zoneId })
  const hasWorkspaces = !!workspacesZone.data?.data?.length
  const zoneFloorIds = new Set(workspacesZone.data?.data.map((workspace) => workspace.floorId))
  const availableFloors = floors.data?.filter((floor) => !zoneId || zoneFloorIds.has(floor.id))

  const workspaceItems = workspaces.data?.data
  const totalNumberOfWorkspaces = workspaceItems?.length

  function workspaceMatchesSearch(workspace: Workspace) {
    return !search || !!workspacesSearch.data?.data.find((searchItem) => searchItem.id === workspace.id)
  }

  function workspaceMatchesZone(workspace: Workspace) {
    return !zoneId || !!workspacesZone.data?.data.find((zoneItem) => zoneItem.id === workspace.id)
  }

  function workspaceIsDisabled(workspace: Workspace) {
    return !workspaceMatchesZone(workspace) || !workspaceMatchesSearch(workspace)
  }

  function workspaceIsPressable(workspace: Workspace) {
    return !!onSelect && !zoneId && workspaceMatchesSearch(workspace)
  }

  function selectWorkspace(workspace: Workspace) {
    return workspaceIsPressable(workspace) ? onSelect?.(workspace) : undefined
  }

  function colorForWorkspace(workspace: Workspace) {
    if (workspaceIsDisabled(workspace)) return Color.graphicGrey
    if (zoneId) return Color.greyBlue
    return colorForWorkspaceStatus(workspace)
  }

  function borderColorForWorkspace(workspace: Workspace) {
    return workspaceIsDisabled(workspace) ? Color.calmGrey : Color.premiumBlack
  }

  useEffect(() => {
    if (!floorId || (zoneId && !availableFloors?.some((floor) => floor.id === floorId))) {
      const firstFloor = availableFloors?.[0]
      firstFloor && onChangeFloor?.(firstFloor.id)
    }
  }, [floorId, zoneId, floors.data, workspacesZone.data?.data, onChangeFloor])

  const floorContainsSearch = search
    ? workspacesSearch.data?.data.length === 0 ||
      workspacesSearch.data?.data.some((workspace) => workspace.floorId === floorId && workspace?.floorplanIsPlaced)
    : true
  useEffect(() => {
    if (!zoneId && !floorContainsSearch) {
      const workspaceFloor = workspacesSearch.data?.data.find((workspace) => workspace.floorplanIsPlaced)
      workspaceFloor?.floorId && onChangeFloor?.(workspaceFloor.floorId)
    }
  }, [workspacesSearch.data?.data])

  return (
    <View style={styles.container}>
      <View style={styles.head}>
        {!zoneId || hasWorkspaces ? (
          <FloorSelect
            companyId={companyId}
            groupBuilding
            floorIds={zoneId ? availableFloors?.map((floor) => floor.id) || [] : undefined}
            value={floorId}
            onChange={(value) => onChangeFloor?.(value as number)}
          />
        ) : (
          <View />
        )}
        <View style={styles.buttons}>
          <Link href={typeHref?.('table') || ''} asChild>
            <Button
              left={<Icon name="menu" size={18} style={styles.iconInactive} />}
              style={styles.button}
              light
              contentContainerStyle={styles.buttonInactive}
              testID="WorkspaceFloorplanSelect.TableButton"
            />
          </Link>
          <Link href={typeHref?.('floorplan') || ''} asChild>
            <Button
              left={<Icon name="map" size={18} />}
              style={styles.button}
              light
              testID="WorkspaceFloorplanSelect.FloorplanButton"
            />
          </Link>
        </View>
      </View>
      {!zoneId || hasWorkspaces ? (
        <>
          <FloorplanZoomView
            style={[styles.floorplan, { height: height - 264 - Spacing.M }]}
            testID="InsightsLiveMap.MapView"
            overlay={zoneId ? undefined : <LegendState style={styles.legend} />}
          >
            <FloorplanView
              companyId={companyId}
              floorId={floorId}
              data={workspaceItems}
              itemColor={(item) => colorForWorkspace(item as Workspace)}
              itemBorderColor={(item) => borderColorForWorkspace(item as Workspace)}
              onItemPress={(item) => selectWorkspace(item as Workspace)}
              itemIsPressable={(item) => workspaceIsPressable(item as Workspace)}
            />
          </FloorplanZoomView>
          {workspaces.isFetching && (
            <Badge
              title={t('Loading workspaces ({{loaded}} of {{total}})...', {
                loaded: workspaceItems?.length,
                total: totalNumberOfWorkspaces,
              })}
              style={styles.loading}
              status="inactive"
            />
          )}
        </>
      ) : (
        <ZoneWorkspaceEmpty onAdd={onAdd} companyId={companyId} />
      )}
      <View />
    </View>
  )
}

const Skeleton = () => {
  const { height } = useWindowDimensions()
  return (
    <View style={styles.container}>
      <View style={styles.head}>
        <Select.Skeleton style={styles.skeletonSelect} />
        <View style={styles.buttons}>
          <Button.Skeleton style={styles.button} contentStyle={styles.skeletonButton} />
          <Button.Skeleton style={styles.button} contentStyle={styles.skeletonButton} />
        </View>
      </View>
      <View style={[styles.floorplan, { height: height - 264 - Spacing.M }, styles.skeletonFloorplan]} />
    </View>
  )
}
WorkspaceFloorplanSelect.Skeleton = Skeleton
export default WorkspaceFloorplanSelect
